/** Utilidades geodésicas para rutas y posiciones GPS. */
export type LngLat = [number, number];

const EARTH_RADIUS_KM = 6371;

function radians(value: number) {
  return (value * Math.PI) / 180;
}

export function distanceKm(a: LngLat, b: LngLat) {
  const dLat = radians(b[1] - a[1]);
  const dLng = radians(b[0] - a[0]);
  const lat1 = radians(a[1]);
  const lat2 = radians(b[1]);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
}

export function bearingDegrees(a: LngLat, b: LngLat) {
  const lng = radians(b[0] - a[0]);
  const lat1 = radians(a[1]);
  const lat2 = radians(b[1]);
  const y = Math.sin(lng) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(lng);
  return (Math.atan2(y, x) * 180) / Math.PI + 360;
}

export function routeLengthKm(points: readonly LngLat[]) {
  return points.slice(1).reduce((sum, point, index) => {
    return sum + distanceKm(points[index]!, point);
  }, 0);
}

export interface RoutePosition {
  coordinate: LngLat;
  bearing: number;
  segmentIndex: number;
  segmentProgress: number;
  travelledKm: number;
  totalKm: number;
}

export function positionAlongRoute(
  points: readonly LngLat[],
  progress: number,
): RoutePosition {
  if (points.length < 2) {
    throw new Error("La ruta debe contener al menos dos puntos.");
  }
  const safeProgress = Math.min(1, Math.max(0, progress));
  const lengths = points
    .slice(1)
    .map((point, index) => distanceKm(points[index]!, point));
  const totalKm = lengths.reduce((sum, length) => sum + length, 0);
  const target = totalKm * safeProgress;
  let travelled = 0;
  for (let index = 0; index < lengths.length; index += 1) {
    const length = lengths[index]!;
    if (travelled + length >= target || index === lengths.length - 1) {
      const segmentProgress = length === 0 ? 0 : (target - travelled) / length;
      const start = points[index]!;
      const end = points[index + 1]!;
      return {
        coordinate: [
          start[0] + (end[0] - start[0]) * segmentProgress,
          start[1] + (end[1] - start[1]) * segmentProgress,
        ],
        bearing: bearingDegrees(start, end) % 360,
        segmentIndex: index,
        segmentProgress,
        travelledKm: target,
        totalKm,
      };
    }
    travelled += length;
  }
  throw new Error("La ruta debe contener al menos dos puntos.");
}

export function splitRoute(
  points: readonly LngLat[],
  segmentIndex: number,
  coordinate: LngLat,
) {
  return {
    completed: [...points.slice(0, segmentIndex + 1), coordinate],
    pending: [coordinate, ...points.slice(segmentIndex + 1)],
  };
}

export function nearestRouteProgress(points: readonly LngLat[], target: LngLat) {
  let nearestIndex = 0;
  let nearestDistance = Number.POSITIVE_INFINITY;
  points.forEach((point, index) => {
    const current = distanceKm(point, target);
    if (current < nearestDistance) {
      nearestDistance = current;
      nearestIndex = index;
    }
  });
  const covered = routeLengthKm(points.slice(0, nearestIndex + 1));
  const total = routeLengthKm(points);
  return { progress: total === 0 ? 0 : covered / total, distanceKm: nearestDistance };
}
